import { create } from "zustand";
import { subscribeWithSelector } from 'zustand/middleware'
import summaryCommonStore from "./summaryCommon.store";
import { getMeetingSummary } from "../services/summary";
import { getShareMeetingSummary } from "@/services/share";
import { FetchSummaryCommonParams, FetchSummaryStreamParams, fetchSummaryStreamsRes, IStreamCloseMap, IStreamLoading, ISummary } from "@/types/summary";
import { postEventSource } from "@/services/eventsource";
import { formatContentSSEData, formatSSEData } from "@/utils/sse";
import { AxiosRequestConfig } from "axios";
import { MeetingRecordVisibility, Origin } from "@/constants/share";
import { meetingContentFormatPattern, SummaryStreamType } from "@/constants/record";
interface SummaryState {
  summary: ISummary | null;
  lastSummary: ISummary | null;
  newSummary: ISummary["completeAiGcContent"] | null;
  summaryLoading: boolean;
  summaryStreamLoading: IStreamLoading;
  streamCloseMap: IStreamCloseMap;
  summaryStreamsAllDone: boolean;
}

interface SummaryStore extends SummaryState {
  fetchSummary: (
    param: {
      streamName: string;
      reload: boolean;
      contentStep: string;
      cover: boolean;
    },
    needThrowError?: boolean,
    config?: AxiosRequestConfig<unknown>
  ) => Promise<ISummary | void>;
  fetchSummaryStream: (summaryStreamType: SummaryStreamType, param: FetchSummaryStreamParams, onStreamClosed: () => void) => () => void;
  fetchSummaryStreams: (param: FetchSummaryCommonParams, onAllStreamsClosed?: () => void) => fetchSummaryStreamsRes;
  setSummaryStreamsAllDone: (summaryStreamsAllDone: boolean) => void;
  modifySummary: (newData: Partial<ISummary["completeAiGcContent"]>) => Promise<void>;
  initNewSummary: () => void;
  setSummaryLoading: (loading: boolean) => void;
  setSummaryStreamLoading: (newStreamLoading: Partial<IStreamLoading>) => Promise<void>;
  setStreamCloseMap: (newStreamCloseMap: Partial<IStreamCloseMap>) => Promise<void>;
  setLastSummary: (summary: ISummary | null) => void;
  resetLastSummary: () => void;
  initStore: () => void;
}

const summaryState: SummaryState = {
  summaryLoading: false,
  summary: null,
  lastSummary: null,
  newSummary: null,
  summaryStreamLoading: {
    meetingSummaryLoading: false,
    contentLoading: false,
    todoLoading: false,
  },
  streamCloseMap: {
    [SummaryStreamType.meetingSummary]: {
      closeStatus: false,
    },
    [SummaryStreamType.content]: {
      closeStatus: false,
    },
    [SummaryStreamType.todo]: {
      closeStatus: false,
    },
  },
  summaryStreamsAllDone: false

};

const summaryStore = create(subscribeWithSelector<SummaryStore>((set, get) => ({
  ...summaryState,
  initStore: () => {
    set({ ...summaryState });
  },
  fetchSummary: async (params, needThrowError = false, config) => {
    try {
      let res;
      const shareInfo = summaryCommonStore.getState().shareInfo;
      const useComplete = shareInfo?.origin === Origin.MEETING_PLATE && shareInfo?.fileTransEnable;
      const formatParams = { ...params, contentStep: useComplete ? "COMPLETE_DOC" : "SRC_DOC" }
      if (shareInfo && summaryCommonStore.getState().shareScope !== MeetingRecordVisibility.orgMember) {
        res = await getShareMeetingSummary({ ...formatParams, ...shareInfo }, config);
      } else {
        res = await getMeetingSummary(formatParams, config);
      }
      if (!res) {
        throw new Error("获取概要失败");
      }
      const targetContent = useComplete ? 'completeAiGcContent' : 'srcAiGcContent';

      if (res[targetContent]?.todo) {
        res[targetContent].todo = res[targetContent]?.todo.map(item => item.replace(meetingContentFormatPattern, ''));
      }
      if(res[targetContent]?.meetingSummary){ 
        res[targetContent].meetingSummary = res[targetContent].meetingSummary.map(item => {
          // 检查是否以数字和点开头
          const listItemPattern = /^(\d+)\.(\s?)(.+)/;
          return item.replace(listItemPattern, (_match, number, space, content) => {
            // 如果已经有空格，保持原样；如果没有空格，添加一个空格
            return `${number}.${space || ' '}${content}`;
          });
        });
      }

      set({ summary: { ...res, srcAiGcContent: res[targetContent] } });
      return res;
    } catch (error) {
      console.log('fetchSummary error', error);
      if (needThrowError) {
        throw error;
      }
    }
  },
  fetchSummaryStream: (summaryStreamType: SummaryStreamType, summaryParam: unknown, onStreamClosed: () => void) => {
    const { setSummaryStreamLoading, setStreamCloseMap } = get();
    setSummaryStreamLoading({
      [`${summaryStreamType}Loading`]: true,
    });
    // 重置状态
    setStreamCloseMap({
      [summaryStreamType]: {
        closeStatus: false,
      },
    });
    const summaryArr: string[] = [];
    const summaryEvSClose = postEventSource(`${import.meta.env.VITE_BACKEND_URL}/api/auth/record-server/meetingSummary/stream`, summaryParam, {
      onmessage: (event) => {
        if (localStorage.getItem("meetingSummaryLog")) {
          console.log(`onmessage ${summaryStreamType}`, event.data);
        }
        if (summaryStreamType === SummaryStreamType.content) {
          formatContentSSEData(event.data, summaryArr);
        } else {
          formatSSEData(event.data, summaryArr);
        }
        // 后台模型现在会返回空字符串，等到推送有内容的时候再关闭loading
        const formatData = summaryArr.filter(item => item);
        if(formatData.length) {
          setSummaryStreamLoading({
            [`${summaryStreamType}Loading`]: false,
          });
        }
        set((state) => ({
          summary: {
            ...(state.summary as ISummary),
            srcAiGcContent: {
              ...(state.summary?.srcAiGcContent as ISummary["completeAiGcContent"]),
              [summaryStreamType]: formatData
            },
          },
        }));
      },
      onsuccess: () => {
        set((state) => ({
          summary: {
            ...(state.summary as ISummary),
            srcAiGcContent: {
              ...(state.summary?.srcAiGcContent as ISummary["completeAiGcContent"]),
              [summaryStreamType]: [],
            },
          },
        }));
      },
      onclose: () => {
        // 兜底措施，防止无论后台没有推送非空内容，没有结束loading问题
        setSummaryStreamLoading({
          [`${summaryStreamType}Loading`]: false,
        });
        setStreamCloseMap({
          [summaryStreamType]: {
            closeStatus: true,
          },
        });
        onStreamClosed();
      },
    });
    setStreamCloseMap({
      [summaryStreamType]: {
        closeMethod: summaryEvSClose,
      },
    });
    return summaryEvSClose;
  },
  setSummaryStreamsAllDone: (summaryStreamsAllDone) => {
    set({ summaryStreamsAllDone })
  },
  fetchSummaryStreams: (params: FetchSummaryCommonParams, onAllStreamsClosed?: () => void) => {
    const { fetchSummaryStream, streamCloseMap } = get();
    const summaryParam = {
      ...params,
      meetingSummaryEvent: "ONE_SEN_SUMMARY",
    };
    const contentParam = {
      ...params,
      meetingSummaryEvent: "SUMMARY",
    };
    const todoParam = {
      ...params,
      meetingSummaryEvent: "TODO",
    };

    const onStreamClosed = () => {
      if (streamCloseMap && Object.values(streamCloseMap).every((item) => item.closeStatus)) {
        onAllStreamsClosed && onAllStreamsClosed();
      }
    };

    const summaryEvSClose = fetchSummaryStream(SummaryStreamType.meetingSummary, summaryParam, onStreamClosed);

    const contentEvSClose = fetchSummaryStream(SummaryStreamType.content, contentParam, onStreamClosed);
    const todoEvSClose = fetchSummaryStream(SummaryStreamType.todo, todoParam, onStreamClosed);
    return { summaryEvSClose, contentEvSClose, todoEvSClose };
  },
  modifySummary: async (newData) => {
    try {
      set((state) => ({
        newSummary: {
          ...(state.newSummary as ISummary["completeAiGcContent"]),
          ...newData,
        },
      }));
    } catch (error) {
      console.error(error);
    }
  },
  initNewSummary: () => {
    const { modifySummary, summary } = get();
    if (!summary) {
      return;
    }

    if (summary.srcAiGcContent?.todo) {
      const formatContent = summary.srcAiGcContent?.todo.map(item => item.replace(meetingContentFormatPattern, ''));
      summary.srcAiGcContent.todo = formatContent;
    }
    modifySummary(summary.srcAiGcContent);
  },
  setSummaryLoading: (loading) => {
    set({ summaryLoading: loading });
  },
  setSummaryStreamLoading: async (newData) => {
    try {
      set((state) => ({
        summaryStreamLoading: {
          ...(state.summaryStreamLoading as IStreamLoading),
          ...newData,
        },
      }));
    } catch (error) {
      console.error("setSummaryStreamLoading error", error);
    }
  },
  setStreamCloseMap: async (newData) => {
    try {
      const { streamCloseMap } = get();
      const key = Object.keys(newData)[0] as SummaryStreamType;
      Object.assign(streamCloseMap[key], newData[key]);
      set({ streamCloseMap });
    } catch (error) {
      console.error("setSummaryStreamCloseMap error", error);
    }
  },
  setLastSummary: (summary) => {
    set({ lastSummary: summary });
  },
  resetLastSummary: () => {
    set({ summary: get().lastSummary, lastSummary: null });
  },
})));

export default summaryStore;
